/**
 * CloudFlare Image Resizing — variant helpers for `imagedelivery.net` URLs.
 *
 * We use FLEXIBLE VARIANTS so the URL itself encodes resize / fit / quality
 * params and we don't have to pre-define named variants in the CloudFlare
 * dashboard.
 *
 *   Input:  https://imagedelivery.net/<HASH>/<IMAGE_ID>/public
 *   Output: https://imagedelivery.net/<HASH>/<IMAGE_ID>/w=256,h=256,fit=cover
 *
 * Gating: flexible variants must be enabled in the CloudFlare dashboard
 *   (Images → Variants → "Allow flexible variants"). When not enabled the
 *   API returns 403. Flip the feature on by setting:
 *
 *      REACT_APP_CF_FLEXIBLE_VARIANTS=true
 *
 *   in /app/frontend/.env. Until then we return the original URL — still
 *   served from the global CDN, just at the master 1024px size.
 *
 * For non-CF URLs (R2, on-pod /api/profile/picture/…) we return the input
 * unchanged — the resize is a no-op.
 */

const IMAGEDELIVERY_PREFIX = 'https://imagedelivery.net/';
const FLEX_ON = process.env.REACT_APP_CF_FLEXIBLE_VARIANTS === 'true';

export function cfResize(url, opts = {}) {
  if (!url || typeof url !== 'string') return url;
  if (!url.startsWith(IMAGEDELIVERY_PREFIX)) return url;
  if (!FLEX_ON) return url;

  const parts = url.split('/');
  if (parts.length < 6) return url;
  parts.pop(); // drop existing variant

  const params = [];
  if (opts.w) params.push(`w=${opts.w}`);
  if (opts.h) params.push(`h=${opts.h}`);
  params.push(`fit=${opts.fit || 'cover'}`);
  if (opts.quality) params.push(`quality=${opts.quality}`);
  params.push(`format=${opts.format || 'auto'}`);

  return parts.join('/') + '/' + params.join(',');
}

/** Preset: 64×64 avatar thumb (sidebar, message bubble). */
export const cfAvatarThumb = (url) => cfResize(url, { w: 64, h: 64 });

/** Preset: 256×256 thumb (discover cards, picker grid). */
export const cfThumb = (url) => cfResize(url, { w: 256, h: 256 });

/** Preset: 512×512 card (profile hero). */
export const cfCard = (url) => cfResize(url, { w: 512, h: 512 });

/** Preset: 1024×1024 full (picker preview). */
export const cfFull = (url) => cfResize(url, { w: 1024, h: 1024 });
